import { PassChangeData } from "@/pages/shift-report/models/ShiftReportResponse";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Box,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { EllipsisCell, formatTons, headerCellSx } from "./utils";
import { getColors } from "@/pages/mill/gantt/components/GanttChart";

type PassChangesTableProps = {
	passChanges: PassChangeData[];
};

export const PassChangesTable = ({ passChanges }: PassChangesTableProps) => {
	if (!passChanges || passChanges.length === 0) {
		return (
			<Box>
				<Typography
					variant="subtitle2"
					sx={{
						fontWeight: 600,
						mb: 1,
						color: "#6a1b9a",
					}}
				>
					Pass Changes
				</Typography>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height={50}
					sx={{ backgroundColor: "#fafafa", borderRadius: 1 }}
				>
					<Typography color="text.secondary" variant="body2">
						No pass changes
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			<Typography
				variant="subtitle2"
				sx={{
					fontWeight: 600,
					mb: 1,
					color: "#6a1b9a",
				}}
			>
				Pass Changes
			</Typography>
			<TableContainer
				component={Paper}
				elevation={0}
				sx={{ overflow: "auto", border: "1px solid #e0e0e0" }}
			>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell sx={headerCellSx}>Stand</TableCell>
							<TableCell sx={headerCellSx}>Carrier</TableCell>
							<TableCell sx={headerCellSx}>Top Roll</TableCell>
							<TableCell sx={headerCellSx}>Bottom Roll</TableCell>
							<TableCell sx={headerCellSx}>Section</TableCell>
							<TableCell sx={headerCellSx}>Previous</TableCell>
							<TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
								Previous Pass Tons
							</TableCell>
							<TableCell sx={headerCellSx}>Current</TableCell>
							<TableCell sx={headerCellSx}>Timestamp</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{[...passChanges]
							.sort((a, b) => b.StandSortOrder - a.StandSortOrder)
							.map((pc, idx) => {
								const sectionColor = getColors(pc.SectionId!);
								return (
									<TableRow key={`${pc.CarrierInstanceId}-${idx}`} hover>
										<TableCell sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
											{pc.StandName || "-"}
										</TableCell>
										<EllipsisCell text={pc.CarrierName || "-"} width={120} />
										<EllipsisCell text={pc.TopRollName || "-"} width={140} />
										<EllipsisCell text={pc.BottomRollName || "-"} width={140} />
										<TableCell sx={{ fontSize: "0.8rem" }}>
											<Chip
												label={pc.SectionName || "-"}
												sx={{
													backgroundColor: sectionColor.fill,
													border: `1px solid ${sectionColor.border}`,
													color: sectionColor.text,
													fontWeight: 600,
													fontSize: "0.75rem",
													height: "22px",
													"& .MuiChip-label": {
														px: 1,
													},
												}}
											/>
										</TableCell>
										<TableCell sx={{ fontSize: "0.8rem" }}>
											{pc.PreviousPass ?? "-"}
										</TableCell>
										<TableCell sx={{ fontSize: "0.8rem" }} align="right">
											{formatTons(pc.PreviousPassTons)}
										</TableCell>
										<TableCell sx={{ fontSize: "0.8rem" }}>
											{pc.CurrentPass}
										</TableCell>
										<TableCell sx={{ fontSize: "0.8rem" }}>
											{dxtToLocalServerTime(pc.Timestamp, "MM/dd HH:mm")}
										</TableCell>
									</TableRow>
								);
							})}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
